<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">
        💰 Bono de Productividad
      </h1>
      <p class="text-gray-600">
        Sistema de bonos mensual del personal de producción e instalaciones
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="cargando" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
      <div class="flex items-center">
        <svg class="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <h3 class="text-red-800 font-semibold">Error al cargar los datos</h3>
          <p class="text-red-600 text-sm">{{ error }}</p>
        </div>
      </div>
      <button @click="cargarDatos" class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
        Reintentar
      </button>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Selector de Período -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <div class="flex flex-wrap items-center gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Mes
            </label>
            <div class="flex gap-2">
              <select 
                v-model="mesSeleccionado" 
                @change="cambiarPeriodo"
                class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option v-for="mes in meses" :key="mes.value" :value="mes.value">
                  {{ mes.label }}
                </option>
              </select>
              <button 
                @click="irMesActual"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Mes Actual
              </button>
            </div>
            <p class="mt-2 text-xs text-gray-500">
              El año actual se usa automáticamente
            </p>
          </div>
          <div class="ml-auto">
            <button 
              @click="actualizarDatos"
              :disabled="cargandoActualizacion"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg v-if="cargandoActualizacion" class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ cargandoActualizacion ? 'Actualizando...' : 'Actualizar' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Dashboard Cards -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <!-- Card Principal - Estado del Bono -->
        <div class="lg:col-span-1">
          <EstadoBono :resultado="datosBono?.resultado" :periodo="datosBono?.periodo" />
        </div>

        <!-- Estadísticas -->
        <div class="lg:col-span-2">
          <EstadisticasBono :estadisticas="datosBono?.estadisticas" />
        </div>
      </div>

      <!-- Gráfica de Progreso -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <GraficaProgreso 
          :estadisticas="datosBono?.estadisticas" 
          :periodo="datosBono?.periodo"
        />
      </div>

      <!-- Detalle de Retrasos -->
      <div v-if="datosBono?.detalle_retrasos && datosBono.detalle_retrasos.length > 0" class="mb-6">
        <DetalleRetrasos :retrasos="datosBono.detalle_retrasos" />
      </div>

      <!-- Historial de Bonos - TEMPORALMENTE DESHABILITADO -->
      <!-- Se habilitará cuando el endpoint de historial esté disponible -->
      <!--
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <HistorialBonos 
          :historial="historial?.historial" 
          :resumen="historial?.resumen"
          @seleccionar-periodo="seleccionarPeriodoDesdeHistorial"
        />
      </div>

      <div class="bg-white rounded-lg shadow-md p-6">
        <GraficaTendencia :historial="historial?.historial" />
      </div>
      -->
      
      <!-- Mensaje informativo sobre el historial -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div class="flex items-start">
          <svg class="w-6 h-6 text-blue-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div>
            <h3 class="text-blue-800 font-semibold mb-1">Historial de Bonos</h3>
            <p class="text-blue-700 text-sm">
              La función de historial estará disponible próximamente. Por ahora puedes consultar bonos de meses específicos usando el selector de período arriba.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import bonoProductividadService from '@/Services/bonoProductividad';
import EstadoBono from './components/EstadoBono.vue';
import EstadisticasBono from './components/EstadisticasBono.vue';
import GraficaProgreso from './components/GraficaProgreso.vue';
import DetalleRetrasos from './components/DetalleRetrasos.vue';
// import HistorialBonos from './components/HistorialBonos.vue'; // Temporalmente deshabilitado
// import GraficaTendencia from './components/GraficaTendencia.vue'; // Temporalmente deshabilitado

export default {
  name: 'BonoProductividad',
  components: {
    EstadoBono,
    EstadisticasBono,
    GraficaProgreso,
    DetalleRetrasos,
    // HistorialBonos, // Temporalmente deshabilitado
    // GraficaTendencia, // Temporalmente deshabilitado
  },
  setup() {
    // Estado
    const cargando = ref(true);
    const cargandoActualizacion = ref(false);
    const error = ref(null);
    const datosBono = ref(null);
    const historial = ref(null);
    
    // Solo mes - el año se maneja automáticamente en el backend
    const mesSeleccionado = ref(new Date().getMonth() + 1);

    // Meses del año
    const meses = [
      { value: 1, label: 'Enero' },
      { value: 2, label: 'Febrero' },
      { value: 3, label: 'Marzo' },
      { value: 4, label: 'Abril' },
      { value: 5, label: 'Mayo' },
      { value: 6, label: 'Junio' },
      { value: 7, label: 'Julio' },
      { value: 8, label: 'Agosto' },
      { value: 9, label: 'Septiembre' },
      { value: 10, label: 'Octubre' },
      { value: 11, label: 'Noviembre' },
      { value: 12, label: 'Diciembre' },
    ];

    // Métodos
    const cargarDatosBono = async () => {
      try {
        // Solo enviar el mes, el backend maneja el año automáticamente
        const response = await bonoProductividadService.calcularBono(
          mesSeleccionado.value
        );

        if (response.error) {
          error.value = response.error;
          return;
        }

        if (response.success) {
          datosBono.value = response.data;
          error.value = null;
        } else {
          error.value = response.message || 'Error al cargar los datos';
        }
      } catch (err) {
        console.error('Error al cargar datos del bono:', err);
        error.value = 'Error de conexión al cargar los datos';
      }
    };

    const cargarHistorial = async () => {
      try {
        const response = await bonoProductividadService.obtenerHistorial(12);
        
        if (response.error) {
          console.error('Error al cargar historial:', response.error);
          return;
        }

        if (response.success) {
          historial.value = response.data;
        }
      } catch (err) {
        console.error('Error al cargar historial:', err);
      }
    };

    const cargarDatos = async () => {
      cargando.value = true;
      error.value = null;
      
      // Solo cargar el bono, no el historial por ahora
      await cargarDatosBono();
      
      cargando.value = false;
    };

    const cambiarPeriodo = () => {
      cargarDatosBono();
    };

    const irMesActual = () => {
      const fechaActual = new Date();
      mesSeleccionado.value = fechaActual.getMonth() + 1;
      cargarDatosBono();
    };

    const actualizarDatos = async () => {
      cargandoActualizacion.value = true;
      await cargarDatos();
      cargandoActualizacion.value = false;
    };

    const seleccionarPeriodoDesdeHistorial = ({ mes }) => {
      mesSeleccionado.value = mes;
      cargarDatosBono();
    };

    // Lifecycle
    onMounted(() => {
      cargarDatos();
    });

    return {
      cargando,
      cargandoActualizacion,
      error,
      datosBono,
      historial,
      mesSeleccionado,
      meses,
      cargarDatos,
      cambiarPeriodo,
      irMesActual,
      actualizarDatos,
      seleccionarPeriodoDesdeHistorial,
    };
  },
};
</script>

<style scoped>
/* Animaciones personalizadas */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active {
  animation: fadeIn 0.3s ease-out;
}
</style>
